import { useState, useMemo } from "react"
import BookCard from "./BookCard"
import BookModal from "./BookModal"

const normalize = (value) => (value || "").toString().toLowerCase()

// Ukrainian plural for "книжка": 1 → книжка, 2–4 → книжки, інше → книжок.
const booksWord = (count) => {
  const mod100 = count % 100
  const mod10 = count % 10
  if (mod100 >= 11 && mod100 <= 14) return "книжок"
  if (mod10 === 1) return "книжка"
  if (mod10 >= 2 && mod10 <= 4) return "книжки"
  return "книжок"
}

// The whole bookshelf screen. Receives books from the server (via the Stimulus
// mount) and owns the "which book is open" state — clicking a card opens the
// modal — plus an instant client-side search over title / author / genre.
const BooksIndex = ({ books }) => {
  const [selected, setSelected] = useState(null)
  const [query, setQuery] = useState("")

  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    if (!q) return books
    return books.filter((book) =>
      `${normalize(book.title)} ${normalize(book.author)} ${normalize(book.genre)}`.includes(q)
    )
  }, [books, q])

  return (
    <>
      <header className="page-head">
        <h1>Книжкова полиця</h1>
        <p className="subtitle">Прочитане, що варто пам'ятати</p>
        <div className="divider" />
        {books.length > 0 && (
          <p className="shelf-count">
            {q ? (
              <>
                Знайдено <strong>{filtered.length}</strong> із {books.length} {booksWord(books.length)}
              </>
            ) : (
              <>
                <strong>{books.length}</strong> {booksWord(books.length)} на полиці
              </>
            )}
          </p>
        )}
      </header>

      {books.length > 0 && (
        <div className="shelf-search">
          <div className="shelf-search__field">
            <svg className="shelf-search__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Пошук за назвою, автором, жанром…"
              aria-label="Пошук книжок"
            />
            {query && (
              <button
                type="button"
                className="shelf-search__clear"
                onClick={() => setQuery("")}
                aria-label="Очистити пошук"
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}

      {books.length === 0 ? (
        <div className="shelf-empty">
          <h2>Полиця поки порожня</h2>
          <p>Додай першу прочитану книжку — і вона стане тут.</p>
        </div>
      ) : filtered.length > 0 ? (
        <section className="shelf">
          {filtered.map((book) => (
            <BookCard key={book.id} book={book} onOpen={() => setSelected(book)} />
          ))}
        </section>
      ) : (
        <div className="shelf-empty">
          <h2>Нічого не знайшлося</h2>
          <p>За запитом «{query.trim()}» книжок немає. Спробуй інакше.</p>
        </div>
      )}

      <div className="shelf-actions">
        <a className="btn btn-primary" href="/books/new">
          ＋ Додати книжку
        </a>
      </div>

      <BookModal book={selected} onClose={() => setSelected(null)} />
    </>
  )
}

export default BooksIndex
